import { useMemo, useState } from 'react'
import { Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { Delete, UploadFile } from '@mui/icons-material'
import { publicAssetUrl } from '../services/api'

export type BrandingKind = 'header' | 'footer' | 'receiptHeader'

const HEADER_MM = [10, 15, 20, 25, 30, 35, 40, 45, 50]
const FOOTER_MM = [10, 15, 20, 25, 30, 35, 40]
const RECEIPT_MM = [15, 20, 25, 30, 35, 40, 45, 50, 55, 60]

const A4_WIDTH_MM = 210
const RECEIPT_WIDTH_MM = 80
const PREVIEW_A4_PX = 520

type Slot = {
  kind: BrandingKind
  title: string
  hint: string
  choices: number[]
  paperWidthMm: number
  heightMm: number
  imageUrl?: string | null
}

type Props = {
  headerUrl?: string | null
  footerUrl?: string | null
  receiptUrl?: string | null
  headerMm: number
  footerMm: number
  receiptMm: number
  cacheKey?: string | number | null
  disabled?: boolean
  onUpload: (kind: BrandingKind, file: File) => Promise<void>
  onDelete: (kind: BrandingKind) => Promise<void>
  onHeightChange: (kind: BrandingKind, mm: number) => Promise<void> | void
}

function snap(value: number, choices: number[], fallback: number) {
  if (!Number.isFinite(value) || value <= 0) return fallback
  return choices.reduce((best, cur) => (Math.abs(cur - value) < Math.abs(best - value) ? cur : best), choices[0])
}

export default function ReportBrandingPanel({
  headerUrl,
  footerUrl,
  receiptUrl,
  headerMm,
  footerMm,
  receiptMm,
  cacheKey,
  disabled,
  onUpload,
  onDelete,
  onHeightChange,
}: Props) {
  const [busyKind, setBusyKind] = useState<BrandingKind | null>(null)
  const [localPreview, setLocalPreview] = useState<Partial<Record<BrandingKind, string>>>({})

  const slots: Slot[] = useMemo(
    () => [
      {
        kind: 'footer',
        title: 'تذييل الطباعة (أسفل الورقة)',
        hint: 'يظهر أسفل كل صفحة A4',
        choices: FOOTER_MM,
        paperWidthMm: A4_WIDTH_MM,
        heightMm: snap(footerMm, FOOTER_MM, 25),
        imageUrl: footerUrl,
      },
      {
        kind: 'receiptHeader',
        title: 'ترويسة فاتورة 80مم',
        hint: 'أعلى ورقة الكاشير الحرارية',
        choices: RECEIPT_MM,
        paperWidthMm: RECEIPT_WIDTH_MM,
        heightMm: snap(receiptMm, RECEIPT_MM, 30),
        imageUrl: receiptUrl,
      },
      {
        kind: 'header',
        title: 'ترويسة الطباعة (أعلى الورقة)',
        hint: 'أعلى كل صفحة A4 — التقارير والفواتير',
        choices: HEADER_MM,
        paperWidthMm: A4_WIDTH_MM,
        heightMm: snap(headerMm, HEADER_MM, 35),
        imageUrl: headerUrl,
      },
    ],
    [footerMm, footerUrl, headerMm, headerUrl, receiptMm, receiptUrl],
  )

  const handlePick = async (kind: BrandingKind, file: File) => {
    const preview = URL.createObjectURL(file)
    setLocalPreview((prev) => ({ ...prev, [kind]: preview }))
    setBusyKind(kind)
    try {
      await onUpload(kind, file)
    } finally {
      setBusyKind(null)
      URL.revokeObjectURL(preview)
      setLocalPreview((prev) => {
        const next = { ...prev }
        delete next[kind]
        return next
      })
    }
  }

  return (
    <Stack spacing={2}>
      {slots.map((slot) => {
        const frameWidth = slot.paperWidthMm === RECEIPT_WIDTH_MM
          ? Math.round(PREVIEW_A4_PX * (RECEIPT_WIDTH_MM / A4_WIDTH_MM))
          : PREVIEW_A4_PX
        const frameHeight = Math.max(36, Math.round(frameWidth * (slot.heightMm / slot.paperWidthMm)))
        const src = localPreview[slot.kind] || publicAssetUrl(slot.imageUrl, cacheKey)
        const busy = busyKind === slot.kind || !!disabled

        return (
          <Paper key={slot.kind} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 800, mb: 0.25 }}>{slot.title}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {slot.hint}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <TextField
                select
                size="small"
                label="الارتفاع"
                value={slot.heightMm}
                disabled={busy}
                sx={{ minWidth: 140 }}
                onChange={(e) => onHeightChange(slot.kind, Number(e.target.value))}
              >
                {slot.choices.map((mm) => (
                  <MenuItem key={mm} value={mm}>
                    {mm} مم
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" component="label" startIcon={<UploadFile />} disabled={busy} size="small">
                {src ? 'تعديل الصورة' : 'رفع صورة'}
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void handlePick(slot.kind, file)
                    e.target.value = ''
                  }}
                />
              </Button>
              {!!src && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  disabled={busy}
                  size="small"
                  onClick={() => {
                    if (!window.confirm('حذف هذه الصورة؟')) return
                    void onDelete(slot.kind)
                  }}
                >
                  حذف
                </Button>
              )}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  width: frameWidth,
                  maxWidth: '100%',
                  height: frameHeight,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {src ? (
                  <Box
                    component="img"
                    src={src}
                    alt={slot.title}
                    sx={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    لا توجد صورة — ارفع ترويسة لتظهر هنا مباشرة
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        )
      })}
    </Stack>
  )
}
